/**
 * Studio workspace state. Holds everything the user is building in
 * one project: script, characters, assets, costumes, locations,
 * casting, storyboard, video status. Persisted to localStorage so a
 * page refresh doesn't blow away the prototype's state.
 *
 * Mirrors `SettingsStore`'s per-key reactive subscribe API — panels
 * subscribe to just the slice they render and re-render on change.
 *
 * All identifiers are short random strings (`base36 timestamp` +
 * 4-char nonce). They're used as React-style keys and as references
 * the LLM passes back via tools (`cast_scene(scene_id, ...)`).
 *
 * ──────────────────────────────────────────────────────────────────
 * FROZEN SHARED CONTRACT (ui-tools fleet).
 * This file is owned by the integrator, NOT by the per-panel agents.
 * Everything added for the studio rebuild is ADDITIVE: old fields stay
 * so the app keeps compiling while each panel migrates to the richer
 * shapes. Panel agents READ these fields and call these mutators; they
 * do not edit this file. If a panel needs a new field/mutator, it is
 * queued to the integrator.
 * ──────────────────────────────────────────────────────────────────
 */

const STORAGE_KEY = "koko_studio_state";
const PROJECT_TITLE_KEY = "koko_studio_project_title";
/** Cross-project location library — persisted independent of the project. */
const GLOBAL_LOCATIONS_KEY = "koko_studio_global_locations";

// --------------------------------------------------------------------
// Structured Final-Draft script model (Agents 01 Script Upload + 02
// Script Editor). A scene's canonical content is `elements[]` when
// present; `body` is kept as a flat back-compat mirror so any panel
// still reading the old string keeps working.
// --------------------------------------------------------------------

export type ScriptElementType =
  | "slug"
  | "action"
  | "character"
  | "dialogue"
  | "parenthetical"
  | "transition";

export interface ScriptElement {
  id: string;
  type: ScriptElementType;
  text: string;
  /** For `character`/`dialogue` lines: the resolved character id, if known. */
  characterId?: string | null;
}

export interface StudioScene {
  id: string;
  /** Optional explicit slug (e.g. "scene-1"); defaults to id-based. */
  heading: string;
  /** Free-form scene body. The "editable document" content (back-compat). */
  body: string;
  /**
   * Structured Final-Draft elements. Canonical when present. Optional
   * during migration — panels that haven't migrated still read `body`.
   */
  elements?: ScriptElement[];
  locationId: string | null;
  dialogueCharacterId: string | null;
  generatedVoiceLine: boolean;
  storyboardReady: boolean;
  videoReady: boolean;
}

/**
 * A character's voice (Characters panel; voice profiles folded in per
 * _GLOBAL.md G2). Either DESIGN a voice from a text description, or
 * reference a CLONED/uploaded voice id. `playbackUrl` caches the last
 * generated preview line ("Hi, I'm {name}").
 */
export interface VoiceProfile {
  /** Text description used to design/generate the voice. */
  design?: string;
  /** Id of an existing/cloned/uploaded voice. */
  clonedVoiceId?: string;
  /** Last generated preview clip (data URL or remote url). */
  playbackUrl?: string | null;
}

/**
 * The FOUR structured character attributes that are the source of truth
 * for cross-app visual consistency (Characters panel). NEVER includes
 * emotion or state of mind. `span` optionally records where the value
 * was tagged inside the free-text description.
 */
export type CharacterAttributeKind =
  | "skin_tone"
  | "hairstyle"
  | "build"
  | "age_range";

export interface CharacterTaggedAttribute {
  attribute: CharacterAttributeKind;
  value: string;
  /** Character offsets into `description` where this value was tagged. */
  span?: { start: number; end: number };
}

export interface StudioCharacter {
  id: string;
  name: string;
  description: string;
  skinTone: string;
  hair: string;
  build: string;
  ageRange: string;
  emotion: string;
  stateOfMind: string;
  /** Optional short role/billing label (e.g. "lead", "ailing father"). */
  role?: string;
  /** Optional uploaded reference image (data URL). No AI gen in scope. */
  referenceImage?: string | null;
  /** Voice (design-a-voice text OR cloned voice id). _GLOBAL.md G2. */
  voiceProfile?: VoiceProfile | null;
  /**
   * The four structured tagged attributes (skin tone, hairstyle, build,
   * age range). Canonical for consistency when present; the flat
   * `skinTone`/`hair`/`build`/`ageRange` fields remain as back-compat.
   */
  taggedAttributes?: CharacterTaggedAttribute[];
  /**
   * Id of this character's PRIMARY costume look (look[0] in
   * `costumeLooksByCharacterId`). Characters own the primary look;
   * Costume owns the secondary looks. _GLOBAL.md G1/costume.md.
   */
  primaryLookId?: string | null;
}

/**
 * DEPRECATED asset taxonomy (assets.md): assets no longer require a
 * type. Retained as a named union for the legacy Assets panel + bridge
 * choreography until that panel is rebuilt.
 */
export type AssetType = "prop" | "vehicle" | "set_piece" | "wardrobe" | "other";

export interface StudioAsset {
  id: string;
  name: string;
  /**
   * DEPRECATED (assets.md): assets no longer require a type — an asset
   * is just name + description. Optional + back-compat only; new code
   * must not depend on it.
   */
  type?: AssetType;
  description: string;
  /**
   * DEPRECATED (G1): usage now comes from @-refs in the script, not a
   * per-asset scene list. Kept for back-compat with old persisted state.
   */
  appearsInScenes?: string[];
  /** Optional uploaded reference image (data URL). */
  referenceImage?: string | null;
}

/**
 * A weather / time-of-day VIEW of the same location (locations.md).
 * Generalizes the old day/night tabs to a full variation system. Each
 * variation carries its own plate image.
 */
export interface LocationVariation {
  id: string;
  /** Human label, e.g. "Night — rain". */
  label: string;
  timeOfDay?: "day" | "night" | "golden-hour" | "dawn" | "dusk" | (string & {});
  weather?: "clear" | "rain" | "snow" | "fog" | "overcast" | (string & {});
  /** Plate image for this variation (data URL). */
  image?: string | null;
}

export interface StudioLocation {
  id: string;
  name: string;
  description: string;
  /** Interior / exterior, read from slug lines when available. */
  intExt?: "INT" | "EXT" | "INT/EXT" | null;
  /** Times of day the SCRIPT uses this location (drives time-of-day tabs). */
  timeOfDay?: string[];
  /** Wide panoramic establishing plate (data URL placeholder for future 3D set). */
  panoramaImage?: string | null;
  /** Optional uploaded scout reference photo (data URL). */
  referenceImage?: string | null;
  /** Weather / time-of-day variation views (locations.md). */
  variations?: LocationVariation[];
  /**
   * If this location was imported from the user's cross-project global
   * library, the source global id (so re-imports dedupe). locations.md.
   */
  globalLocationId?: string | null;
}

export interface MoodBoardItem {
  id: string;
  kind: "image" | "palette" | "note" | "lyric";
  title: string;
  detail: string;
  /** Freeform-pinboard placement + size (fractions or px — agent's choice). */
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  /** Image tile data URL. */
  imageData?: string | null;
  /** Palette tile swatches (hex). */
  palette?: string[];
}

export interface SoundDesignState {
  mode: "simple" | "advanced";
  stylePills: string[];
  vocalGender: "neutral" | "feminine" | "masculine";
  weirdness: number;
  styleInfluence: number;
  sceneLengthSec: number;
}

/** Per-scene audio override layered over the global sound design. */
export interface SceneSoundOverride {
  music?: string | null;
  sfx?: string | null;
  silent?: boolean;
}

/** A single wardrobe look for a character, covering a scene range. */
export interface CostumeLook {
  id: string;
  note: string;
  /** Inclusive scene-index range, e.g. [0,2] for sc.1–3; null end = "onward". */
  sceneFrom?: number | null;
  sceneTo?: number | null;
  /** Explicit scene ids if non-contiguous. */
  sceneIds?: string[];
  /** Reference image (data URL). */
  referenceImage?: string | null;
}

/** A single storyboard shot frame within a scene. */
export interface StoryboardFrame {
  id: string;
  /** Action / dialogue caption shown under the frame. */
  caption: string;
  /** Frame image (data URL) — null until generated/uploaded. */
  image?: string | null;
  order: number;
  retakes?: number;
  ready?: boolean;
}

/** Derived film-wide visual direction the mood board produces. */
export interface VisualDirection {
  palette: string[];
  notes: string[];
  accent: string | null;
}

/**
 * An `@`-reference typed into the script (_GLOBAL.md G1). These are the
 * CANONICAL record of "what entity is used in which scene", replacing
 * the old per-card scene-assignment chips. Owned by the Script Editor
 * agent (autocomplete + rendering); the integrator owns the shared model
 * + the ref→scene index here.
 */
export type EntityRefType = "character" | "location" | "look" | "asset";

export interface EntityRef {
  id: string;
  type: EntityRefType;
  /** Id of the referenced entity (characterId / locationId / lookId / assetId). */
  entityId: string;
  /** Scene this `@`-ref lives in. */
  sceneId: string;
  /** Optional script element the ref sits in (script-editor owned). */
  elementId?: string | null;
  /** Optional column offset within the element text. */
  column?: number | null;
}

/**
 * The shared CLIP/CUT model arranged by the Timeline and played back by
 * Final Video (timeline.md / final-video.md). One canonical list; both
 * surfaces read + mutate it through the store.
 */
export type StudioClipType = "video" | "audio" | "text" | "subtitle";

export interface StudioClip {
  id: string;
  /** Scene this clip belongs to (if scene-bound). */
  sceneId?: string | null;
  /** Storyboard frame this clip was built from (final-video.md). */
  frameId?: string | null;
  /** Position on the time axis (seconds). */
  startSec: number;
  /** Length on the time axis (seconds). */
  durationSec: number;
  type: StudioClipType;
  /** Render payload — image still, audio waveform, or text/subtitle. */
  content?: {
    image?: string | null;
    waveform?: string | null;
    text?: string | null;
  };
  /** How many times this clip has been re-shot. */
  retakeCount?: number;
  /** Optional reference video guiding a retake. */
  referenceVideo?: string | null;
  /** Whether this clip is rendered/ready in the cut. */
  ready?: boolean;
}

/**
 * Global background score (Timeline owns the UI; store just holds it).
 * _GLOBAL.md G8: the one global score control moved to the Timeline.
 */
export interface BackgroundScore {
  /** Style/genre label, e.g. "cinematic", "lo-fi pulse". */
  style: string;
  /** Intensity 0–100. */
  intensity: number;
  /** Optional generated track (data URL or remote url). */
  trackUrl?: string | null;
}

export interface StudioState {
  /**
   * Filename of the most recently uploaded script PDF, if any. The
   * actual file bytes don't round-trip through localStorage (too
   * heavy); we just remember that *a* script was uploaded so the
   * checklist can light up the "Script" step.
   */
  scriptFilename: string | null;
  /** Parsed/edited scenes. Empty when no script has been uploaded. */
  scenes: StudioScene[];
  characters: StudioCharacter[];
  assets: StudioAsset[];
  locations: StudioLocation[];
  moodBoardItems: MoodBoardItem[];
  soundDesign: SoundDesignState;
  cameraBySceneId: Record<
    string,
    {
      x: number;
      y: number;
      z: number;
      lensMm: number;
    }
  >;
  retakeCountBySceneId: Record<string, number>;
  /** sceneId → list of character ids cast in that scene. */
  castingBySceneId: Record<string, string[]>;
  /**
   * characterId → costume notes (back-compat flat string). New panels
   * use `costumeLooksByCharacterId` for per-scene looks.
   */
  costumeByCharacterId: Record<string, string>;
  /** characterId → wardrobe looks (per-scene-range). */
  costumeLooksByCharacterId: Record<string, CostumeLook[]>;
  /** sceneId → storyboard shot frames. */
  storyboardFramesBySceneId: Record<string, StoryboardFrame[]>;
  /** sceneId → audio override layered over the global sound design. */
  sceneSoundOverrides: Record<string, SceneSoundOverride>;
  /** Global background score (Timeline-owned UI; _GLOBAL.md G8). */
  backgroundScore: BackgroundScore;
  /**
   * Canonical `@`-references typed into the script (_GLOBAL.md G1).
   * Read READ-ONLY by Timeline / gap detection via the ref→scene index.
   */
  entityRefs: EntityRef[];
  /**
   * Shared timeline/cut clips (timeline.md / final-video.md). One list
   * arranged by Timeline and played by Final Video.
   */
  clips: StudioClip[];
  /**
   * Per-shot location-variation assignment: storyboard frame id →
   * location variation id (locations.md shot-level assignment).
   */
  shotVariationByFrameId: Record<string, string>;
  /** The scene currently in focus across script / timeline / camera. */
  activeSceneId: string | null;
  /**
   * Storyboard generation status. The prototype doesn't really
   * generate anything, so this just toggles between "empty" and
   * "ready" based on whether the user has visited the panel and
   * clicked "generate".
   */
  storyboardStatus: "empty" | "ready";
  videoStatus: "empty" | "rendering" | "ready";
}

export type StudioStateKey = keyof StudioState;

function buildEmptyState(): StudioState {
  return {
    scriptFilename: null,
    scenes: [],
    characters: [],
    assets: [],
    locations: [],
    moodBoardItems: [],
    soundDesign: {
      mode: "simple",
      stylePills: ["cinematic", "warm strings", "moody pulse"],
      vocalGender: "neutral",
      weirdness: 35,
      styleInfluence: 65,
      sceneLengthSec: 12,
    },
    cameraBySceneId: {},
    retakeCountBySceneId: {},
    castingBySceneId: {},
    costumeByCharacterId: {},
    costumeLooksByCharacterId: {},
    storyboardFramesBySceneId: {},
    sceneSoundOverrides: {},
    backgroundScore: {
      style: "cinematic",
      intensity: 60,
      trackUrl: null,
    },
    entityRefs: [],
    clips: [],
    shotVariationByFrameId: {},
    activeSceneId: null,
    storyboardStatus: "empty",
    videoStatus: "empty",
  };
}

/** Payload for ingesting a freshly parsed script in one atomic write. */
export interface ParsedScriptIngest {
  filename: string;
  scenes: Array<{
    heading?: string;
    body?: string;
    elements?: ScriptElement[];
    locationName?: string | null;
    intExt?: StudioLocation["intExt"];
    timeOfDay?: string | null;
  }>;
  characters?: Array<{ name: string; description?: string }>;
  locations?: Array<{
    name: string;
    description?: string;
    intExt?: StudioLocation["intExt"];
    timeOfDay?: string[];
  }>;
  assets?: Array<{ name: string; type?: StudioAsset["type"]; description?: string }>;
}

type Listener<K extends StudioStateKey> = (
  next: StudioState[K],
  previous: StudioState[K],
) => void;

export class StudioStore {
  private state: StudioState;
  private projectTitle: string;
  private readonly listeners = new Map<
    StudioStateKey | "__projectTitle__",
    Set<(next: unknown, previous: unknown) => void>
  >();

  constructor() {
    this.state = { ...buildEmptyState(), ...this.readPersistedState() };
    this.projectTitle = this.readPersistedTitle() ?? "Untitled film";
  }

  // ------------------------------------------------------------------
  // Reads
  // ------------------------------------------------------------------

  get<K extends StudioStateKey>(key: K): StudioState[K] {
    return this.state[key];
  }

  snapshot(): Readonly<StudioState> {
    return this.state;
  }

  getProjectTitle(): string {
    return this.projectTitle;
  }

  /** The scene currently in focus, or null. */
  getActiveScene(): StudioScene | null {
    const id = this.state.activeSceneId;
    if (!id) return null;
    return this.state.scenes.find((scene) => scene.id === id) ?? null;
  }

  /**
   * The cast of a scene, AUTO-DERIVED from script dialogue (_GLOBAL.md
   * G2 — the Casting panel was dropped). Combines:
   *   - characters that speak/are cued in the scene's structured
   *     `elements` (resolved `characterId` on character/dialogue lines),
   *   - any `@character` refs in that scene,
   *   - the legacy manual `castingBySceneId` override (still honored).
   * Returns only ids that still exist as characters. Read by Timeline +
   * Script Editor.
   */
  getCastingForScene(sceneId: string): string[] {
    const scene = this.state.scenes.find((s) => s.id === sceneId);
    if (!scene) return [];
    const ids = new Set<string>();
    for (const id of this.state.castingBySceneId[sceneId] ?? []) ids.add(id);
    for (const el of scene.elements ?? []) {
      if (
        (el.type === "character" || el.type === "dialogue") &&
        el.characterId
      ) {
        ids.add(el.characterId);
      }
    }
    for (const ref of this.state.entityRefs) {
      if (ref.sceneId === sceneId && ref.type === "character") {
        ids.add(ref.entityId);
      }
    }
    return Array.from(ids).filter((id) =>
      this.state.characters.some((c) => c.id === id),
    );
  }

  /** The global background score (Timeline-owned UI; _GLOBAL.md G8). */
  getBackgroundScore(): BackgroundScore {
    return this.state.backgroundScore;
  }

  /** All `@`-refs that live in a given scene (ref→scene index; G1). */
  getRefsForScene(sceneId: string): EntityRef[] {
    return this.state.entityRefs.filter((ref) => ref.sceneId === sceneId);
  }

  /**
   * Scene ids an entity is referenced in (derived usage; _GLOBAL.md G1).
   * Unions `@`-refs with slug-driven location links so Timeline / gap
   * detection can show usage READ-ONLY without per-card chips.
   */
  getScenesForEntity(entityId: string): string[] {
    const sceneIds = new Set<string>();
    for (const ref of this.state.entityRefs) {
      if (ref.entityId === entityId) sceneIds.add(ref.sceneId);
    }
    for (const scene of this.state.scenes) {
      if (scene.locationId === entityId) sceneIds.add(scene.id);
    }
    return Array.from(sceneIds);
  }

  /** All clips that belong to a scene (Timeline ↔ Final Video). */
  getClipsForScene(sceneId: string): StudioClip[] {
    return this.state.clips.filter((clip) => clip.sceneId === sceneId);
  }

  /**
   * Film-wide visual direction derived from the mood board (palette
   * swatches + note tiles). Read by storyboard / location / asset
   * panels for their (stubbed) generation prompts and by the studio
   * accent tint. Pure derivation — no stored duplicate.
   */
  getVisualDirection(): VisualDirection {
    const palette: string[] = [];
    const notes: string[] = [];
    for (const item of this.state.moodBoardItems) {
      if (Array.isArray(item.palette)) palette.push(...item.palette);
      if (item.kind === "note" || item.kind === "lyric") {
        const text = `${item.title} ${item.detail}`.trim();
        if (text) notes.push(text);
      }
    }
    return {
      palette: Array.from(new Set(palette)).slice(0, 8),
      notes: notes.slice(0, 12),
      accent: palette[0] ?? null,
    };
  }

  /**
   * The next incomplete pipeline step, in production order, or null
   * when everything's done. Drives the checklist "next" nudge and the
   * Koko bird's recommendation. Kept deliberately simple.
   */
  getNextStep(): { stepId: string; label: string } | null {
    const s = this.state;
    const has = (n: number) => n > 0;
    if (!s.scriptFilename && s.scenes.length === 0)
      return { stepId: "script_upload", label: "Upload or write your script" };
    if (s.scenes.length === 0)
      return { stepId: "script_editor", label: "Write your first scene" };
    if (!has(s.characters.length))
      return { stepId: "characters", label: "Create your characters" };
    if (!has(s.locations.length))
      return { stepId: "location", label: "Scout your locations" };
    if (s.storyboardStatus !== "ready")
      return { stepId: "storyboard", label: "Board your scenes" };
    if (s.videoStatus !== "ready")
      return { stepId: "final_video", label: "Render your film" };
    return null;
  }

  /**
   * Compact human-readable summary used in the studio system prompt
   * so Claude always knows the current workspace state without us
   * having to push individual fields into the prompt template.
   */
  buildPromptSummary(): string {
    const lines: string[] = [];
    lines.push(`Project title: ${this.projectTitle}`);
    lines.push(
      this.state.scriptFilename
        ? `Script: uploaded (${this.state.scriptFilename}), ${this.state.scenes.length} scene(s)`
        : "Script: none uploaded yet",
    );
    lines.push(
      `Characters (${this.state.characters.length}): ${
        this.state.characters
          .map((character) => `${character.id}=${character.name}`)
          .join(", ") || "none"
      }`,
    );
    lines.push(
      `Assets (${this.state.assets.length}): ${
        this.state.assets
          .map((asset) => `${asset.id}=${asset.name} (${asset.type})`)
          .join(", ") || "none"
      }`,
    );
    lines.push(
      `Locations (${this.state.locations.length}): ${
        this.state.locations
          .map((location) => `${location.id}=${location.name}`)
          .join(", ") || "none"
      }`,
    );
    lines.push(`Mood board items: ${this.state.moodBoardItems.length}`);
    lines.push(
      `Background score: ${this.state.backgroundScore.style} @ ${this.state.backgroundScore.intensity}%`,
    );
    // Casting is auto-derived from the script now (no Casting panel).
    const castSceneCount = this.state.scenes.filter(
      (scene) => this.getCastingForScene(scene.id).length > 0,
    ).length;
    lines.push(
      `Casting (auto from script): ${castSceneCount}/${this.state.scenes.length} scene(s) populated`,
    );
    const costumedCharacterCount = this.state.characters.filter((character) => {
      const flat = (this.state.costumeByCharacterId[character.id] ?? "").trim();
      const looks = this.state.costumeLooksByCharacterId[character.id] ?? [];
      return flat.length > 0 || looks.length > 0;
    }).length;
    lines.push(
      `Costumes: ${costumedCharacterCount}/${this.state.characters.length} character(s) styled`,
    );
    lines.push(`Storyboard: ${this.state.storyboardStatus}`);
    lines.push(`Final video: ${this.state.videoStatus}`);
    const next = this.getNextStep();
    if (next) lines.push(`Next suggested step: ${next.stepId} — ${next.label}`);
    return lines.join("\n");
  }

  // ------------------------------------------------------------------
  // Writes — typed mutators
  // ------------------------------------------------------------------

  setProjectTitle(nextTitle: string): void {
    const trimmed = nextTitle.trim();
    if (trimmed.length === 0) return;
    if (trimmed === this.projectTitle) return;
    const previous = this.projectTitle;
    this.projectTitle = trimmed;
    this.persistTitle();
    this.notify("__projectTitle__", trimmed, previous);
  }

  setScript(filename: string, initialScenes: StudioScene[]): void {
    const normalizedScenes = initialScenes.map((scene) => ({
      ...scene,
      locationId: scene.locationId ?? null,
      dialogueCharacterId: scene.dialogueCharacterId ?? null,
      generatedVoiceLine: !!scene.generatedVoiceLine,
      storyboardReady: !!scene.storyboardReady,
      videoReady: !!scene.videoReady,
    }));
    this.update({
      scriptFilename: filename,
      scenes: normalizedScenes.length > 0 ? normalizedScenes : this.state.scenes,
    });
  }

  /**
   * Atomically ingest a freshly parsed script: writes scenes (with
   * structured elements), and auto-creates the unique characters,
   * locations, and assets the parser found. Used by Agent 01 (Script
   * Upload). Locations are created first so scenes can link to them.
   */
  ingestParsedScript(payload: ParsedScriptIngest): void {
    // Locations first (scenes link by id).
    const locationByName = new Map<string, string>();
    const locations: StudioLocation[] = [...this.state.locations];
    const ensureLocation = (
      name: string,
      extra: Partial<StudioLocation> = {},
    ): string => {
      const key = name.trim().toLowerCase();
      if (!key) return "";
      const existing =
        locationByName.get(key) ??
        locations.find((l) => l.name.trim().toLowerCase() === key)?.id;
      if (existing) {
        locationByName.set(key, existing);
        return existing;
      }
      const created: StudioLocation = {
        id: generateId("lo"),
        name: name.trim(),
        description: extra.description ?? "",
        intExt: extra.intExt ?? null,
        timeOfDay: extra.timeOfDay ?? [],
        panoramaImage: null,
        referenceImage: null,
      };
      locations.push(created);
      locationByName.set(key, created.id);
      return created.id;
    };
    for (const loc of payload.locations ?? []) {
      ensureLocation(loc.name, {
        description: loc.description,
        intExt: loc.intExt,
        timeOfDay: loc.timeOfDay,
      });
    }

    // Characters (dedupe by name).
    const characters: StudioCharacter[] = [...this.state.characters];
    const characterByName = new Map<string, string>(
      characters.map((c) => [c.name.trim().toLowerCase(), c.id]),
    );
    for (const ch of payload.characters ?? []) {
      const key = ch.name.trim().toLowerCase();
      if (!key || characterByName.has(key)) continue;
      const created: StudioCharacter = {
        id: generateId("ch"),
        name: ch.name.trim(),
        description: ch.description ?? "",
        skinTone: "medium",
        hair: "wavy dark shoulder length",
        build: "average",
        ageRange: "mid 20s to mid 30s",
        emotion: "",
        stateOfMind: "",
        referenceImage: null,
      };
      characters.push(created);
      characterByName.set(key, created.id);
    }

    // Assets (dedupe by name).
    const assets: StudioAsset[] = [...this.state.assets];
    const assetByName = new Set(assets.map((a) => a.name.trim().toLowerCase()));
    for (const as of payload.assets ?? []) {
      const key = as.name.trim().toLowerCase();
      if (!key || assetByName.has(key)) continue;
      assets.push({
        id: generateId("as"),
        name: as.name.trim(),
        type: as.type ?? "prop",
        description: as.description ?? "",
        appearsInScenes: [],
        referenceImage: null,
      });
      assetByName.add(key);
    }

    // Scenes last, linking to locations + resolving dialogue character ids.
    const scenes: StudioScene[] = payload.scenes.map((scene, index) => {
      const locId = scene.locationName
        ? ensureLocation(scene.locationName, {
            intExt: scene.intExt,
            timeOfDay: scene.timeOfDay ? [scene.timeOfDay] : [],
          })
        : null;
      const elements = (scene.elements ?? []).map((el) => ({
        ...el,
        id: el.id ?? generateId("el"),
        characterId:
          el.characterId ??
          (el.type === "character" || el.type === "dialogue"
            ? characterByName.get(el.text.trim().toLowerCase()) ?? null
            : null),
      }));
      const body =
        scene.body ?? elements.map((el) => el.text).join("\n");
      return {
        id: generateId("sc"),
        heading: scene.heading ?? `SCENE ${index + 1}`,
        body,
        elements: elements.length > 0 ? elements : undefined,
        locationId: locId || null,
        dialogueCharacterId: null,
        generatedVoiceLine: false,
        storyboardReady: false,
        videoReady: false,
      };
    });

    this.update({
      scriptFilename: payload.filename,
      scenes: scenes.length > 0 ? scenes : this.state.scenes,
      locations,
      characters,
      assets,
    });
  }

  addScene(body: string): StudioScene {
    const newScene: StudioScene = {
      id: generateId("sc"),
      heading: `SCENE ${this.state.scenes.length + 1}`,
      body,
      locationId: this.state.locations[0]?.id ?? null,
      dialogueCharacterId: this.state.characters[0]?.id ?? null,
      generatedVoiceLine: false,
      storyboardReady: false,
      videoReady: false,
    };
    this.update({ scenes: [...this.state.scenes, newScene] });
    return newScene;
  }

  updateScene(
    sceneId: string,
    body: string,
    patch: Partial<
      Pick<
        StudioScene,
        "locationId" | "dialogueCharacterId" | "generatedVoiceLine" | "storyboardReady" | "videoReady" | "elements" | "heading"
      >
    > = {},
  ): boolean {
    const idx = this.state.scenes.findIndex((scene) => scene.id === sceneId);
    if (idx === -1) return false;
    const next = [...this.state.scenes];
    next[idx] = { ...next[idx], body, ...patch };
    this.update({ scenes: next });
    return true;
  }

  /** Set a scene's structured Final-Draft elements (and mirror to body). */
  setSceneElements(sceneId: string, elements: ScriptElement[]): boolean {
    const idx = this.state.scenes.findIndex((scene) => scene.id === sceneId);
    if (idx === -1) return false;
    const next = [...this.state.scenes];
    next[idx] = {
      ...next[idx],
      elements,
      body: elements.map((el) => el.text).join("\n"),
      generatedVoiceLine: false,
      storyboardReady: false,
      videoReady: false,
    };
    this.update({ scenes: next });
    return true;
  }

  deleteScene(sceneId: string): boolean {
    if (!this.state.scenes.some((scene) => scene.id === sceneId)) return false;
    const nextCasting = { ...this.state.castingBySceneId };
    delete nextCasting[sceneId];
    const nextFrames = { ...this.state.storyboardFramesBySceneId };
    delete nextFrames[sceneId];
    const nextSound = { ...this.state.sceneSoundOverrides };
    delete nextSound[sceneId];
    this.update({
      scenes: this.state.scenes.filter((scene) => scene.id !== sceneId),
      castingBySceneId: nextCasting,
      storyboardFramesBySceneId: nextFrames,
      sceneSoundOverrides: nextSound,
      activeSceneId:
        this.state.activeSceneId === sceneId ? null : this.state.activeSceneId,
      cameraBySceneId: Object.fromEntries(
        Object.entries(this.state.cameraBySceneId).filter(([id]) => id !== sceneId),
      ),
      retakeCountBySceneId: Object.fromEntries(
        Object.entries(this.state.retakeCountBySceneId).filter(([id]) => id !== sceneId),
      ),
    });
    return true;
  }

  setActiveScene(sceneId: string | null): void {
    if (sceneId === this.state.activeSceneId) return;
    if (sceneId !== null && !this.state.scenes.some((s) => s.id === sceneId)) {
      return;
    }
    this.update({ activeSceneId: sceneId });
  }

  addCharacter(
    name: string,
    description: string,
    optional: Partial<
      Pick<
        StudioCharacter,
        "skinTone" | "hair" | "build" | "ageRange" | "emotion" | "stateOfMind" | "role" | "referenceImage"
      >
    > = {},
  ): StudioCharacter {
    const newCharacter: StudioCharacter = {
      id: generateId("ch"),
      name,
      description,
      skinTone: optional.skinTone ?? "medium",
      hair: optional.hair ?? "wavy dark shoulder length",
      build: optional.build ?? "average",
      ageRange: optional.ageRange ?? "mid 20s to mid 30s",
      emotion: optional.emotion ?? "",
      stateOfMind: optional.stateOfMind ?? "",
      role: optional.role,
      referenceImage: optional.referenceImage ?? null,
    };
    this.update({ characters: [...this.state.characters, newCharacter] });
    return newCharacter;
  }

  updateCharacter(
    characterId: string,
    patch: Partial<
      Pick<
        StudioCharacter,
        "name" | "description" | "skinTone" | "hair" | "build" | "ageRange" | "emotion" | "stateOfMind" | "role" | "referenceImage" | "voiceProfile" | "taggedAttributes" | "primaryLookId"
      >
    >,
  ): boolean {
    const idx = this.state.characters.findIndex(
      (character) => character.id === characterId,
    );
    if (idx === -1) return false;
    const next = [...this.state.characters];
    next[idx] = { ...next[idx], ...patch };
    this.update({ characters: next });
    return true;
  }

  deleteCharacter(characterId: string): boolean {
    if (!this.state.characters.some((character) => character.id === characterId)) {
      return false;
    }
    const nextCostumes = { ...this.state.costumeByCharacterId };
    delete nextCostumes[characterId];
    const nextLooks = { ...this.state.costumeLooksByCharacterId };
    delete nextLooks[characterId];
    const nextCasting: Record<string, string[]> = {};
    for (const [sceneId, ids] of Object.entries(this.state.castingBySceneId)) {
      nextCasting[sceneId] = ids.filter((id) => id !== characterId);
    }
    this.update({
      characters: this.state.characters.filter(
        (character) => character.id !== characterId,
      ),
      costumeByCharacterId: nextCostumes,
      costumeLooksByCharacterId: nextLooks,
      castingBySceneId: nextCasting,
    });
    return true;
  }

  addAsset(
    name: string,
    type: StudioAsset["type"],
    description: string,
  ): StudioAsset {
    const newAsset: StudioAsset = {
      id: generateId("as"),
      name,
      type,
      description,
      appearsInScenes: [],
      referenceImage: null,
    };
    this.update({ assets: [...this.state.assets, newAsset] });
    return newAsset;
  }

  updateAsset(
    assetId: string,
    patch: Partial<Pick<StudioAsset, "name" | "type" | "description" | "appearsInScenes" | "referenceImage">>,
  ): boolean {
    const idx = this.state.assets.findIndex((asset) => asset.id === assetId);
    if (idx === -1) return false;
    const next = [...this.state.assets];
    next[idx] = { ...next[idx], ...patch };
    this.update({ assets: next });
    return true;
  }

  deleteAsset(assetId: string): boolean {
    if (!this.state.assets.some((asset) => asset.id === assetId)) return false;
    this.update({
      assets: this.state.assets.filter((asset) => asset.id !== assetId),
    });
    return true;
  }

  addLocation(name: string, description: string): StudioLocation {
    const newLocation: StudioLocation = {
      id: generateId("lo"),
      name,
      description,
      intExt: null,
      timeOfDay: [],
      panoramaImage: null,
      referenceImage: null,
    };
    this.update({ locations: [...this.state.locations, newLocation] });
    return newLocation;
  }

  updateLocation(
    locationId: string,
    patch: Partial<Pick<StudioLocation, "name" | "description" | "intExt" | "timeOfDay" | "panoramaImage" | "referenceImage" | "variations" | "globalLocationId">>,
  ): boolean {
    const idx = this.state.locations.findIndex(
      (location) => location.id === locationId,
    );
    if (idx === -1) return false;
    const next = [...this.state.locations];
    next[idx] = { ...next[idx], ...patch };
    this.update({ locations: next });
    return true;
  }

  deleteLocation(locationId: string): boolean {
    if (!this.state.locations.some((location) => location.id === locationId)) {
      return false;
    }
    const nextScenes = this.state.scenes.map((scene) =>
      scene.locationId === locationId ? { ...scene, locationId: null } : scene,
    );
    this.update({
      locations: this.state.locations.filter(
        (location) => location.id !== locationId,
      ),
      scenes: nextScenes,
    });
    return true;
  }

  addMoodBoardItem(
    kind: MoodBoardItem["kind"],
    title: string,
    detail: string,
    extra: Partial<Pick<MoodBoardItem, "x" | "y" | "w" | "h" | "imageData" | "palette">> = {},
  ): MoodBoardItem {
    const item: MoodBoardItem = {
      id: generateId("mb"),
      kind,
      title,
      detail,
      ...extra,
    };
    this.update({ moodBoardItems: [...this.state.moodBoardItems, item] });
    return item;
  }

  updateMoodBoardItem(
    itemId: string,
    patch: Partial<Pick<MoodBoardItem, "title" | "detail" | "x" | "y" | "w" | "h" | "imageData" | "palette" | "kind">>,
  ): boolean {
    const idx = this.state.moodBoardItems.findIndex((item) => item.id === itemId);
    if (idx === -1) return false;
    const next = [...this.state.moodBoardItems];
    next[idx] = { ...next[idx], ...patch };
    this.update({ moodBoardItems: next });
    return true;
  }

  deleteMoodBoardItem(itemId: string): boolean {
    if (!this.state.moodBoardItems.some((item) => item.id === itemId)) {
      return false;
    }
    this.update({
      moodBoardItems: this.state.moodBoardItems.filter((item) => item.id !== itemId),
    });
    return true;
  }

  updateSoundDesign(patch: Partial<SoundDesignState>): void {
    this.update({
      soundDesign: { ...this.state.soundDesign, ...patch },
    });
  }

  /** Set (or clear) a per-scene audio override. Pass null to clear. */
  setSceneSoundOverride(sceneId: string, override: SceneSoundOverride | null): boolean {
    if (!this.state.scenes.some((scene) => scene.id === sceneId)) return false;
    const next = { ...this.state.sceneSoundOverrides };
    if (override === null) delete next[sceneId];
    else next[sceneId] = override;
    this.update({ sceneSoundOverrides: next });
    return true;
  }

  setSceneCamera(
    sceneId: string,
    camera: {
      x: number;
      y: number;
      z: number;
      lensMm: number;
    },
  ): boolean {
    if (!this.state.scenes.some((scene) => scene.id === sceneId)) return false;
    this.update({
      cameraBySceneId: {
        ...this.state.cameraBySceneId,
        [sceneId]: camera,
      },
    });
    return true;
  }

  requestSceneRetake(sceneId: string): boolean {
    const scene = this.state.scenes.find((entry) => entry.id === sceneId);
    if (!scene) return false;
    const current = this.state.retakeCountBySceneId[sceneId] ?? 0;
    this.update({
      retakeCountBySceneId: {
        ...this.state.retakeCountBySceneId,
        [sceneId]: current + 1,
      },
      scenes: this.state.scenes.map((entry) =>
        entry.id === sceneId ? { ...entry, videoReady: false } : entry,
      ),
      videoStatus: "empty",
    });
    return true;
  }

  setCostume(characterId: string, notes: string): boolean {
    if (!this.state.characters.some((character) => character.id === characterId)) {
      return false;
    }
    this.update({
      costumeByCharacterId: {
        ...this.state.costumeByCharacterId,
        [characterId]: notes,
      },
    });
    return true;
  }

  /** Replace the full set of wardrobe looks for a character. */
  setCostumeLooks(characterId: string, looks: CostumeLook[]): boolean {
    if (!this.state.characters.some((character) => character.id === characterId)) {
      return false;
    }
    this.update({
      costumeLooksByCharacterId: {
        ...this.state.costumeLooksByCharacterId,
        [characterId]: looks,
      },
    });
    return true;
  }

  /** Replace the storyboard shot frames for a scene. */
  setStoryboardFrames(sceneId: string, frames: StoryboardFrame[]): boolean {
    if (!this.state.scenes.some((scene) => scene.id === sceneId)) return false;
    this.update({
      storyboardFramesBySceneId: {
        ...this.state.storyboardFramesBySceneId,
        [sceneId]: frames,
      },
    });
    return true;
  }

  setCasting(sceneId: string, characterIds: string[]): boolean {
    if (!this.state.scenes.some((scene) => scene.id === sceneId)) return false;
    // Filter to only ids that actually exist as characters; skip dupes.
    const validUniqueIds = Array.from(
      new Set(
        characterIds.filter((id) =>
          this.state.characters.some((character) => character.id === id),
        ),
      ),
    );
    this.update({
      castingBySceneId: {
        ...this.state.castingBySceneId,
        [sceneId]: validUniqueIds,
      },
    });
    return true;
  }

  // ------------------------------------------------------------------
  // Background score (Timeline-owned UI; _GLOBAL.md G8)
  // ------------------------------------------------------------------

  setBackgroundScore(patch: Partial<BackgroundScore>): void {
    this.update({
      backgroundScore: { ...this.state.backgroundScore, ...patch },
    });
  }

  // ------------------------------------------------------------------
  // @-references (canonical usage record; _GLOBAL.md G1)
  // ------------------------------------------------------------------

  /** Replace the entire `@`-ref list (Script Editor re-derives on edit). */
  setEntityRefs(refs: EntityRef[]): void {
    this.update({ entityRefs: refs });
  }

  /** Add one `@`-ref; returns the created ref (with a generated id). */
  addEntityRef(ref: Omit<EntityRef, "id"> & { id?: string }): EntityRef {
    const created: EntityRef = { ...ref, id: ref.id ?? generateId("ref") };
    this.update({ entityRefs: [...this.state.entityRefs, created] });
    return created;
  }

  removeEntityRef(refId: string): boolean {
    if (!this.state.entityRefs.some((r) => r.id === refId)) return false;
    this.update({
      entityRefs: this.state.entityRefs.filter((r) => r.id !== refId),
    });
    return true;
  }

  /** Replace all refs for a single scene (Script Editor re-parse). */
  replaceSceneRefs(sceneId: string, refs: Array<Omit<EntityRef, "id" | "sceneId"> & { id?: string }>): void {
    const kept = this.state.entityRefs.filter((r) => r.sceneId !== sceneId);
    const next = refs.map((r) => ({
      ...r,
      id: r.id ?? generateId("ref"),
      sceneId,
    }));
    this.update({ entityRefs: [...kept, ...next] });
  }

  // ------------------------------------------------------------------
  // Shared clip/cut model (Timeline ↔ Final Video)
  // ------------------------------------------------------------------

  setClips(clips: StudioClip[]): void {
    this.update({ clips });
  }

  addClip(clip: Omit<StudioClip, "id"> & { id?: string }): StudioClip {
    const created: StudioClip = { ...clip, id: clip.id ?? generateId("clip") };
    this.update({ clips: [...this.state.clips, created] });
    return created;
  }

  updateClip(clipId: string, patch: Partial<Omit<StudioClip, "id">>): boolean {
    const idx = this.state.clips.findIndex((c) => c.id === clipId);
    if (idx === -1) return false;
    const next = [...this.state.clips];
    next[idx] = { ...next[idx], ...patch };
    this.update({ clips: next });
    return true;
  }

  deleteClip(clipId: string): boolean {
    if (!this.state.clips.some((c) => c.id === clipId)) return false;
    this.update({ clips: this.state.clips.filter((c) => c.id !== clipId) });
    return true;
  }

  /** Bump a clip's retake count, optionally attaching a reference video. */
  retakeClip(clipId: string, referenceVideo?: string | null): boolean {
    const idx = this.state.clips.findIndex((c) => c.id === clipId);
    if (idx === -1) return false;
    const next = [...this.state.clips];
    next[idx] = {
      ...next[idx],
      retakeCount: (next[idx].retakeCount ?? 0) + 1,
      ready: false,
      referenceVideo:
        referenceVideo === undefined ? next[idx].referenceVideo : referenceVideo,
    };
    this.update({ clips: next });
    return true;
  }

  // ------------------------------------------------------------------
  // Location variations + shot-level assignment (locations.md)
  // ------------------------------------------------------------------

  /** Replace a location's weather/time-of-day variation views. */
  setLocationVariations(locationId: string, variations: LocationVariation[]): boolean {
    return this.updateLocation(locationId, { variations });
  }

  /** Assign (or clear) a location VARIATION to a single storyboard shot/frame. */
  setShotVariation(frameId: string, variationId: string | null): void {
    const next = { ...this.state.shotVariationByFrameId };
    if (variationId === null) delete next[frameId];
    else next[frameId] = variationId;
    this.update({ shotVariationByFrameId: next });
  }

  // ------------------------------------------------------------------
  // Cross-project global location library (locations.md)
  // ------------------------------------------------------------------

  /** Locations saved across the user's projects (separate persistence). */
  getGlobalLocations(): StudioLocation[] {
    return this.readGlobalLocations();
  }

  /** Save a project location into the cross-project global library. */
  saveLocationToGlobal(locationId: string): StudioLocation | null {
    const loc = this.state.locations.find((l) => l.id === locationId);
    if (!loc) return null;
    const lib = this.readGlobalLocations();
    const globalLocationId = loc.globalLocationId ?? loc.id;
    const entry: StudioLocation = { ...loc, globalLocationId };
    const existingIdx = lib.findIndex((l) => l.globalLocationId === entry.globalLocationId);
    if (existingIdx === -1) lib.push(entry);
    else lib[existingIdx] = entry;
    this.persistGlobalLocations(lib);
    // Stamp the link back onto the SOURCE location so the project copy
    // knows it lives in the global library (Locations agent worked around
    // this locally; do it canonically here).
    if (loc.globalLocationId !== globalLocationId) {
      this.update({
        locations: this.state.locations.map((l) =>
          l.id === locationId ? { ...l, globalLocationId } : l,
        ),
      });
    }
    return entry;
  }

  /** Import a global-library location into the current project (deduped). */
  importGlobalLocation(globalLocationId: string): StudioLocation | null {
    const lib = this.readGlobalLocations();
    const src = lib.find((l) => l.globalLocationId === globalLocationId);
    if (!src) return null;
    const already = this.state.locations.find(
      (l) => l.globalLocationId === globalLocationId,
    );
    if (already) return already;
    const created: StudioLocation = {
      ...src,
      id: generateId("lo"),
      globalLocationId,
    };
    this.update({ locations: [...this.state.locations, created] });
    return created;
  }

  // ------------------------------------------------------------------
  // Living-document auto-create (typing/agent introduces an entity; G6)
  // ------------------------------------------------------------------

  /** Find-or-create a character by name (case-insensitive dedupe). */
  autoCreateCharacter(name: string, description = ""): StudioCharacter {
    const key = name.trim().toLowerCase();
    const existing = this.state.characters.find(
      (c) => c.name.trim().toLowerCase() === key,
    );
    if (existing) return existing;
    return this.addCharacter(name.trim(), description);
  }

  /** Find-or-create a location by name (case-insensitive dedupe). */
  autoCreateLocation(
    name: string,
    extra: Partial<Pick<StudioLocation, "description" | "intExt" | "timeOfDay">> = {},
  ): StudioLocation {
    const key = name.trim().toLowerCase();
    const existing = this.state.locations.find(
      (l) => l.name.trim().toLowerCase() === key,
    );
    if (existing) return existing;
    const created: StudioLocation = {
      id: generateId("lo"),
      name: name.trim(),
      description: extra.description ?? "",
      intExt: extra.intExt ?? null,
      timeOfDay: extra.timeOfDay ?? [],
      panoramaImage: null,
      referenceImage: null,
      variations: [],
      globalLocationId: null,
    };
    this.update({ locations: [...this.state.locations, created] });
    return created;
  }

  /** Find-or-create a (type-less) asset by name (case-insensitive dedupe). */
  autoCreateAsset(name: string, description = ""): StudioAsset {
    const key = name.trim().toLowerCase();
    const existing = this.state.assets.find(
      (a) => a.name.trim().toLowerCase() === key,
    );
    if (existing) return existing;
    const created: StudioAsset = {
      id: generateId("as"),
      name: name.trim(),
      description,
      referenceImage: null,
    };
    this.update({ assets: [...this.state.assets, created] });
    return created;
  }

  setStoryboardStatus(status: StudioState["storyboardStatus"]): void {
    if (status === this.state.storyboardStatus) return;
    this.update({ storyboardStatus: status });
  }

  setVideoStatus(status: StudioState["videoStatus"]): void {
    if (status === this.state.videoStatus) return;
    this.update({ videoStatus: status });
  }

  /** Wipe everything — useful for a "new project" affordance. */
  resetAll(): void {
    const empty = buildEmptyState();
    const previousState = this.state;
    this.state = empty;
    this.persistState();
    // Notify every key that the value changed.
    (Object.keys(empty) as StudioStateKey[]).forEach((key) => {
      this.notify(key, empty[key], previousState[key]);
    });
  }

  // ------------------------------------------------------------------
  // Subscriptions
  // ------------------------------------------------------------------

  subscribe<K extends StudioStateKey>(key: K, listener: Listener<K>): () => void {
    let bucket = this.listeners.get(key);
    if (!bucket) {
      bucket = new Set();
      this.listeners.set(key, bucket);
    }
    bucket.add(listener as (next: unknown, previous: unknown) => void);
    return () => {
      bucket!.delete(listener as (next: unknown, previous: unknown) => void);
    };
  }

  subscribeProjectTitle(listener: (next: string, previous: string) => void): () => void {
    let bucket = this.listeners.get("__projectTitle__");
    if (!bucket) {
      bucket = new Set();
      this.listeners.set("__projectTitle__", bucket);
    }
    bucket.add(listener as (next: unknown, previous: unknown) => void);
    return () => {
      bucket!.delete(listener as (next: unknown, previous: unknown) => void);
    };
  }

  // ------------------------------------------------------------------
  // Internal
  // ------------------------------------------------------------------

  /**
   * Apply a partial state patch, persist, then notify listeners for
   * each key whose value actually changed (reference inequality).
   * Avoids spuriously re-rendering panels when an unrelated slice
   * was the only thing touched.
   */
  private update(patch: Partial<StudioState>): void {
    const previousState = this.state;
    this.state = { ...previousState, ...patch };
    this.persistState();
    (Object.keys(patch) as StudioStateKey[]).forEach((key) => {
      const nextValue = this.state[key];
      const previousValue = previousState[key];
      if (nextValue !== previousValue) {
        this.notify(key, nextValue, previousValue);
      }
    });
  }

  private notify(
    key: StudioStateKey | "__projectTitle__",
    nextValue: unknown,
    previousValue: unknown,
  ): void {
    const bucket = this.listeners.get(key);
    if (!bucket) return;
    bucket.forEach((listener) => {
      try {
        listener(nextValue, previousValue);
      } catch (error) {
        console.warn(`[studio] listener for ${String(key)} threw:`, error);
      }
    });
  }

  private readPersistedState(): Partial<StudioState> {
    if (typeof window === "undefined" || !window.localStorage) return {};
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        const state = parsed as Partial<StudioState>;
        // Spread the persisted state so EVERY field round-trips (costume
        // looks, location variations/globalLocationId, asset type, clips,
        // backgroundScore, entityRefs, shotVariationByFrameId, etc.).
        const result: Partial<StudioState> = { ...state };
        // Scenes/characters get per-item defaults for required fields, but
        // we spread the original item FIRST so newer fields survive.
        if (Array.isArray(state.scenes)) {
          result.scenes = state.scenes.map((scene) => ({
            ...scene,
            id: scene.id ?? generateId("sc"),
            heading: scene.heading ?? "SCENE",
            body: scene.body ?? "",
            elements: Array.isArray(scene.elements) ? scene.elements : undefined,
            locationId: scene.locationId ?? null,
            dialogueCharacterId: scene.dialogueCharacterId ?? null,
            generatedVoiceLine: !!scene.generatedVoiceLine,
            storyboardReady: !!scene.storyboardReady,
            videoReady: !!scene.videoReady,
          }));
        } else {
          // Partial seed without scenes: drop the key so buildEmptyState's
          // [] default survives (undefined would clobber it → .length crash).
          delete result.scenes;
        }
        if (Array.isArray(state.characters)) {
          result.characters = state.characters.map((character) => ({
            ...character,
            id: character.id ?? generateId("ch"),
            name: character.name ?? "Unnamed",
            description: character.description ?? "",
            skinTone: character.skinTone ?? "medium",
            hair: character.hair ?? "wavy dark shoulder length",
            build: character.build ?? "average",
            ageRange: character.ageRange ?? "mid 20s to mid 30s",
            emotion: character.emotion ?? "",
            stateOfMind: character.stateOfMind ?? "",
            role: character.role,
            referenceImage: character.referenceImage ?? null,
            // Merged Characters panel fields (voice + tagging + primary look).
            voiceProfile: character.voiceProfile ?? null,
            taggedAttributes: Array.isArray(character.taggedAttributes)
              ? character.taggedAttributes
              : [],
            primaryLookId: character.primaryLookId ?? null,
          }));
        } else {
          delete result.characters;
        }
        // Guard every other collection default: a malformed/partial seed
        // must never replace a buildEmptyState default with a non-collection
        // value (would crash on .map/.length/spread at boot).
        const empty = buildEmptyState();
        for (const key of Object.keys(empty) as StudioStateKey[]) {
          const def = empty[key];
          const val = (result as Record<string, unknown>)[key];
          if (Array.isArray(def) && !Array.isArray(val)) {
            delete (result as Record<string, unknown>)[key];
          } else if (
            def !== null &&
            typeof def === "object" &&
            !Array.isArray(def) &&
            (val === null || typeof val !== "object")
          ) {
            delete (result as Record<string, unknown>)[key];
          }
        }
        return result;
      }
    } catch {
      // Corrupt entry — fall back to empty state.
    }
    return {};
  }

  private persistState(): void {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      // Quota — silently ignore.
    }
  }

  private readGlobalLocations(): StudioLocation[] {
    if (typeof window === "undefined" || !window.localStorage) return [];
    const raw = window.localStorage.getItem(GLOBAL_LOCATIONS_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as StudioLocation[]) : [];
    } catch {
      return [];
    }
  }

  private persistGlobalLocations(locations: StudioLocation[]): void {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      window.localStorage.setItem(GLOBAL_LOCATIONS_KEY, JSON.stringify(locations));
    } catch {
      // Quota — silently ignore.
    }
  }

  private readPersistedTitle(): string | null {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage.getItem(PROJECT_TITLE_KEY);
  }

  private persistTitle(): void {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      window.localStorage.setItem(PROJECT_TITLE_KEY, this.projectTitle);
    } catch {
      // ignore
    }
  }
}

/**
 * Generate a short, sortable id with a kind prefix. Not cryptographic;
 * just unique enough for client-only state.
 */
function generateId(prefix: string): string {
  const timeSlug = Date.now().toString(36);
  const randomSlug = Math.random().toString(36).slice(2, 6);
  return `${prefix}_${timeSlug}${randomSlug}`;
}
